<div class="flex flex-col gap-6 animate-fade-in">

  <!-- Header Section -->
  <div>
    <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-[var(--text-muted)]">Insights & Analytics</h1>
    <p class="text-[var(--text-muted)] mt-1">Deep dive into global performance and audience engagement.</p>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    
    <!-- Big Line Chart -->
    <div class="lg:col-span-2 bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 relative overflow-hidden flex flex-col min-h-[400px]">
      <div class="flex justify-between items-center mb-6 z-10">
        <h2 class="text-xl font-semibold text-white">Global Traffic Trends</h2>
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <span class="w-3 h-3 rounded-full bg-[#506ef7]"></span>
            Current Year
          </div>
          <div class="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <span class="w-3 h-3 rounded-full border border-gray-500"></span>
            Previous Year
          </div>
        </div>
      </div>
      
      <!-- Graph mockup abstract -->
      <div class="flex-1 w-full relative pt-10 px-2 flex items-end opacity-90">
        <!-- SVG Abstract Line Mockup -->
        <svg class="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
          <path d="M0 80 Q 20 60, 40 70 T 80 40 T 100 20 L 100 100 L 0 100 Z" fill="url(#grad1)" opacity="0.3"></path>
          <path d="M0 80 Q 20 60, 40 70 T 80 40 T 100 20" fill="none" class="stroke-[#506ef7]" stroke-width="2" vector-effect="non-scaling-stroke"></path>
          
          <path d="M0 90 Q 30 80, 50 85 T 90 60 T 100 50" fill="none" stroke="#6b7280" stroke-width="2" stroke-dasharray="4" vector-effect="non-scaling-stroke" opacity="0.5"></path>
          
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:#506ef7;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#1e1e1e;stop-opacity:0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div class="flex justify-between text-xs text-[var(--text-muted)] mt-4 px-2 relative z-10 border-t border-[#ffffff10] pt-4">
        <span>Week 1</span><span>Week 2</span><span>Week 3</span><span>Week 4</span>
      </div>
    </div>

    <!-- Right Side Data -->
    <div class="flex flex-col gap-6">
      <!-- Donut Chart summary -->
      <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 flex flex-col justify-center items-center">
        <h2 class="text-lg font-semibold text-white w-full text-left mb-6">Device Distribution</h2>
        
        <div class="relative w-40 h-40 flex items-center justify-center">
          <svg class="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
            <!-- Mobile 60% -->
            <path class="text-[#506ef7]" stroke-dasharray="60, 100" stroke="currentColor" stroke-width="4" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"></path>
            <!-- Desktop 30% -->
            <path class="text-blue-400" stroke-dasharray="30, 100" stroke-dashoffset="-60" stroke="currentColor" stroke-width="4" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"></path>
            <!-- Tablet 10% -->
            <path class="text-indigo-300" stroke-dasharray="10, 100" stroke-dashoffset="-90" stroke="currentColor" stroke-width="4" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"></path>
          </svg>
          <div class="absolute flex flex-col items-center">
            <span class="text-2xl font-bold text-white">5.3M</span>
            <span class="text-xs text-[var(--text-muted)]">Total Users</span>
          </div>
        </div>

        <div class="mt-6 w-full flex flex-col gap-3">
          <div class="flex justify-between items-center text-sm">
            <div class="flex items-center gap-2"><span class="w-2.5 h-2.5 rounded-full bg-[#506ef7]"></span><span class="text-white">Mobile</span></div>
            <span class="text-white font-medium">60%</span>
          </div>
          <div class="flex justify-between items-center text-sm">
            <div class="flex items-center gap-2"><span class="w-2.5 h-2.5 rounded-full bg-blue-400"></span><span class="text-white">Desktop</span></div>
            <span class="text-white font-medium">30%</span>
          </div>
          <div class="flex justify-between items-center text-sm">
            <div class="flex items-center gap-2"><span class="w-2.5 h-2.5 rounded-full bg-indigo-300"></span><span class="text-white">Tablet</span></div>
            <span class="text-white font-medium">10%</span>
          </div>
        </div>
      </div>

      <!-- Region Breakdown -->
      <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 flex flex-col flex-1">
        <h2 class="text-lg font-semibold text-white mb-6">Top Regions</h2>
        <div class="flex flex-col gap-5">
          @for (region of topRegions; track region.name) {
            <div>
              <div class="flex justify-between text-sm mb-1.5">
                <span class="text-white font-medium">{{ region.name }}</span>
                <span class="text-[var(--text-muted)] block">{{ region.amount }}</span>
              </div>
              <div class="w-full h-2 bg-[#ffffff05] rounded-full overflow-hidden">
                <div class="h-full rounded-full transition-all duration-1000 ease-out" [class]="region.color" [style.width]="region.value"></div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>

</div>
